/**
 * SSN Security Utilities for Frontend
 * 
 * This module provides:
 * - SSN validation
 * - SSN formatting/masking for display
 * - Integration with backend SSN tokenization
 * 
 * Note: Actual encryption happens on the backend using AES-256-GCM.
 * The frontend handles:
 * 1. Input validation
 * 2. Display of masked SSNs
 * 3. Token management
 */

/**
 * Validate SSN format
 * @param {string} ssn - The SSN to validate
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export const validateSSN = (ssn) => {
  if (!ssn) {
    return { isValid: false, error: 'SSN is required' };
  }
  
  // Remove formatting
  const cleaned = ssn.replace(/[^0-9]/g, '');
  
  if (cleaned.length !== 9) {
    return { isValid: false, error: 'SSN must be exactly 9 digits' };
  }
  
  // Check for invalid patterns (all same digit, sequential)
  if (/^(\d)\1{8}$/.test(cleaned)) {
    return { isValid: false, error: 'Invalid SSN format' };
  }
  
  // Check for invalid area numbers (000, 666, 900-999)
  const area = parseInt(cleaned.substring(0, 3), 10);
  if (area === 0 || area === 666 || area >= 900) {
    return { isValid: false, error: 'Invalid SSN area number' };
  }
  
  // Check for invalid group number (00)
  const group = parseInt(cleaned.substring(3, 5), 10);
  if (group === 0) {
    return { isValid: false, error: 'Invalid SSN group number' };
  }
  
  // Check for invalid serial number (0000)
  const serial = parseInt(cleaned.substring(5, 9), 10);
  if (serial === 0) {
    return { isValid: false, error: 'Invalid SSN serial number' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Format SSN for display (XXX-XX-XXXX)
 * @param {string} ssn - The SSN to format
 * @returns {string} Formatted SSN
 */
export const formatSSN = (ssn) => {
  if (!ssn) return '';
  const cleaned = ssn.replace(/[^0-9]/g, '');
  if (cleaned.length !== 9) return ssn;
  return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 5)}-${cleaned.substring(5, 9)}`;
};

/**
 * Mask SSN for secure display (XXX-XX-1234)
 * @param {string} ssn - The SSN to mask (can be full SSN or already masked)
 * @returns {string} Masked SSN showing only last 4 digits
 */
export const maskSSN = (ssn) => {
  if (!ssn) return 'XXX-XX-XXXX';
  
  // If already masked, return as-is
  if (ssn.startsWith('XXX-XX-')) return ssn;
  
  const cleaned = ssn.replace(/[^0-9]/g, '');
  if (cleaned.length < 4) return 'XXX-XX-XXXX';
  
  return `XXX-XX-${cleaned.slice(-4)}`;
};

/**
 * Check if a value is an SSN token (UUID format)
 * @param {string} value - The value to check
 * @returns {boolean} True if value is a UUID token
 */
export const isSSNToken = (value) => {
  if (!value || typeof value !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

/**
 * Check if personal_info has secured SSN
 * @param {Object} personalInfo - The personal info object
 * @returns {boolean} True if SSN is secured
 */
export const hasSecuredSSN = (personalInfo) => {
  return personalInfo?.ssn_secured === true && personalInfo?.ssn_token;
};

/**
 * Get display SSN from personal_info
 * Returns masked SSN if available, or masks the raw SSN
 * @param {Object} personalInfo - The personal info object
 * @returns {string} Masked SSN for display
 */
export const getDisplaySSN = (personalInfo) => {
  if (!personalInfo) return 'XXX-XX-XXXX';
  
  // If SSN is secured, the ssn field should already be masked
  if (personalInfo.ssn_secured) {
    return personalInfo.ssn || 'XXX-XX-XXXX';
  }
  
  // If not secured, mask the raw SSN
  return maskSSN(personalInfo.ssn);
};

/**
 * SSN input handler - restricts to numbers only and max 9 digits
 * @param {string} value - The input value
 * @returns {string} Cleaned SSN (digits only, max 9)
 */
export const handleSSNInput = (value) => {
  return value.replace(/[^0-9]/g, '').slice(0, 9);
};

/**
 * Format SSN as user types (XXX-XX-XXXX)
 * @param {string} value - The input value
 * @returns {string} Formatted SSN
 */
export const formatSSNInput = (value) => {
  const cleaned = value.replace(/[^0-9]/g, '');
  
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 5) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  } else {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  }
};

/**
 * SSN Security Status Component Data
 * Returns status info for displaying SSN security state
 * @param {Object} personalInfo - The personal info object
 * @returns {Object} { isSecured, statusText, statusColor }
 */
export const getSSNSecurityStatus = (personalInfo) => {
  if (hasSecuredSSN(personalInfo)) {
    return {
      isSecured: true,
      statusText: 'Encrypted & Secured',
      statusColor: 'text-green-600',
      icon: 'shield-check'
    };
  }
  
  if (personalInfo?.ssn) {
    return {
      isSecured: false,
      statusText: 'Will be encrypted on save',
      statusColor: 'text-yellow-600',
      icon: 'shield'
    };
  }
  
  return {
    isSecured: false,
    statusText: 'Not provided',
    statusColor: 'text-gray-400',
    icon: 'shield-off'
  };
};
